import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAddPayerSchema } from "@features/AddPayerModal/useAddPayerSchema";
import { Button } from "@components/Button/Button";
import { HouseIcon, LetterIcon, PersonPlusIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormPhoneInput } from "@components/inputs/form/FormPhoneInput/FormPhoneInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCreateCustomerWithPermissionsMutation } from "@services/customer/mutations";
import { CustomerServiceRelationRole } from "@services/customer/types";
import { useOverviewQuery } from "@services/overview/queries";
import { isNullOrUndefined } from "@utils/common";

interface IFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  jointBillServiceId: number;
  customerServiceRelationRole: CustomerServiceRelationRole;
  agreed?: boolean;
}

export const AddPayerModal: React.FC<IUseModalProps> = (props) => {
  const { t } = useTranslation();
  const modal = useModal(props);
  const schema = useAddPayerSchema();
  const { data: overview } = useOverviewQuery();
  const { mutateAsync: createCustomerWithPermissions, isPending } =
    useCreateCustomerWithPermissionsMutation();
  const jointBillServiceIdOptions = (overview?.jointBillServices ?? [])
    .filter((jbs) => jbs.isOwner)
    .map((jbs) => ({
      label: jbs.displayName ?? t("common.defaultAddress"),
      value: jbs.jointBillServiceId,
    }));
  const customerServiceRelationRoleOptions = [
    CustomerServiceRelationRole.PAYER,
    CustomerServiceRelationRole.OWNER,
  ].map((value) => ({
    label: t(`common.customerServiceRelationRole.${value}`),
    value,
  }));
  const { handleSubmit, control, setError } = useForm<IFormData>({
    defaultValues: {
      jointBillServiceId: jointBillServiceIdOptions[0]?.value,
      customerServiceRelationRole: CustomerServiceRelationRole.PAYER,
    },
    resolver: zodResolver(schema),
  });
  const { setApiErrors } = useApiErrorUtils(setError);

  const [customerServiceRelationRole] = useWatch({
    name: ["customerServiceRelationRole"],
    control,
  });

  const addPayer: SubmitHandler<IFormData> = async (data) => {
    delete data.agreed;

    const response =
      await createCustomerWithPermissions(data).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      toast.success(t("addPayerModal.toast.success"));
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: PersonPlusIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("addPayerModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(addPayer)}
            className="w-full"
            isLoading={isPending}
          >
            {t("addPayerModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("addPayerModal.title")}
      subtitle={t("addPayerModal.subtitle")}
      className="md:max-w-xl"
    >
      <form onSubmit={handleSubmit(addPayer)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <FormInput
            label={t("addPayerModal.form.fields.firstName.label")}
            name="firstName"
            control={control}
          />
          <FormInput
            label={t("addPayerModal.form.fields.lastName.label")}
            name="lastName"
            control={control}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <FormInput
            label={t("addPayerModal.form.fields.email.label")}
            name="email"
            control={control}
            iconLeft={LetterIcon}
          />
          <FormPhoneInput
            label={t("addPayerModal.form.fields.phoneNumber.label")}
            name="phoneNumber"
            control={control}
          />
        </div>
        <FormSelect
          label={t("addPayerModal.form.fields.jointBillServiceId.label")}
          name="jointBillServiceId"
          control={control}
          options={jointBillServiceIdOptions}
          icon={HouseIcon}
        />
        <FormSelect
          label={t(
            "addPayerModal.form.fields.customerServiceRelationRole.label",
          )}
          name="customerServiceRelationRole"
          control={control}
          options={customerServiceRelationRoleOptions}
        />
        <FormCheckbox
          label={t(
            `addPayerModal.form.fields.agreed.labels.${customerServiceRelationRole}`,
          )}
          name="agreed"
          control={control}
        />
      </form>
    </Modal>
  );
};
